/**
 * Focus & selection overlay — draws the keyboard cursor cell, the dashed ring
 * around the event under the cursor, and the solid ring around the selected
 * event. Lives ABOVE the columns so selection/focus changes never re-render
 * a single event block. Geometry is pure math from the indexes; horizontal
 * placement uses CSS var arithmetic so viewport resizes need no React work.
 */
import React from "react";
import { MCEvent, MCGridConfig, MCInteractionState } from "../model/types";
import { MCEventIndex } from "../model/eventIndex";
import { yForMin } from "../model/gridGeometry";
import { MiniStore, useMiniSelector } from "../model/miniStore";

interface FocusOverlayProps {
  cfg: MCGridConfig;
  store: MiniStore<MCInteractionState>;
  index: MCEventIndex;
  resourceIndexById: ReadonlyMap<string, number>;
}

const eventRectStyle = (
  cfg: MCGridConfig,
  event: MCEvent,
  colIndex: number,
): React.CSSProperties => {
  const laneFraction = 1 / event.laneCount;
  const top = yForMin(cfg, event.startMin);
  return {
    left: `calc(var(--mc-col-w) * ${colIndex + event.lane * laneFraction})`,
    width: `calc(var(--mc-col-w) * ${laneFraction} - 2px)`,
    top,
    height: Math.max(yForMin(cfg, event.endMin) - top - 1, cfg.slotHeightPx - 1),
  };
};

const FocusOverlay: React.FC<FocusOverlayProps> = ({ cfg, store, index, resourceIndexById }) => {
  const focus = useMiniSelector(store, (s) => s.focus);
  const focusedEventId = useMiniSelector(store, (s) => s.focusedEventId);
  const selectedEventId = useMiniSelector(store, (s) => s.selectedEventId);
  const isMoving = useMiniSelector(store, (s) => Boolean(s.move));
  const hasRange = useMiniSelector(store, (s) => Boolean(s.range));

  // Move mode: ghosts own the canvas; a live range rect covers the cursor.
  if (isMoving) return null;

  const rings: React.ReactNode[] = [];

  const pushRing = (id: string | null, variant: "cursor" | "selected") => {
    if (!id) return;
    const event = index.byId.get(id);
    if (!event) return;
    const colIndex = resourceIndexById.get(event.resourceId);
    if (colIndex == null) return;
    rings.push(
      <div
        key={`${variant}-${id}`}
        className={`mc-ring mc-ring--${variant}`}
        style={eventRectStyle(cfg, event, colIndex)}
      />,
    );
  };

  // Selected ring persists; cursor ring only when it differs from selection.
  pushRing(selectedEventId, "selected");
  if (focusedEventId !== selectedEventId) pushRing(focusedEventId, "cursor");

  return (
    <>
      {focus && !focusedEventId && !hasRange && (
        <div
          className="mc-focus-cell"
          style={{
            left: `calc(var(--mc-col-w) * ${focus.resourceIndex} + 1px)`,
            width: "calc(var(--mc-col-w) - 3px)",
            top: yForMin(cfg, focus.slotMin),
            height: cfg.slotHeightPx,
          }}
        />
      )}
      {rings}
    </>
  );
};

export default React.memo(FocusOverlay);
